/** Inclusive ISO-8601 datetime bounds (typically from local start/end of day). */
export type IsoDateTimeRange = {
  fromDate: string;
  toDate: string;
};

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * Last calendar day of a month.
 * @param month 1–12
 */
export function lastDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

/**
 * @param month 1–12
 * @returns `YYYY-MM-DD`
 */
export function formatCalendarDay(
  year: number,
  month: number,
  day: number,
): string {
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

/**
 * @param month 1–12
 * @returns `YYYY-MM`
 */
export function formatCalendarMonth(year: number, month: number): string {
  return `${year}-${pad2(month)}`;
}

/** @returns `YYYY-MM-DD` for the local calendar date of `date`. */
export function toCalendarDay(date: Date): string {
  return formatCalendarDay(
    date.getFullYear(),
    date.getMonth() + 1,
    date.getDate(),
  );
}

/** @returns `YYYY-MM` for the local calendar month of `date`. */
export function toCalendarMonth(date: Date): string {
  return formatCalendarMonth(date.getFullYear(), date.getMonth() + 1);
}

/**
 * Normalize a day key to `YYYY-MM-DD`.
 * Accepts `YYYY-MM-DD` or an ISO datetime (uses the date prefix).
 */
export function normalizeCalendarDay(value: string): string {
  return value.slice(0, 10);
}

/**
 * Normalize a month key to `YYYY-MM`.
 * Accepts `YYYY-MM`, `YYYY-MM-DD`, or an ISO datetime (uses the month prefix).
 */
export function normalizeCalendarMonth(value: string): string {
  return value.slice(0, 7);
}

/**
 * Parse a calendar day as local midnight.
 * @param day `YYYY-MM-DD`
 */
export function parseLocalCalendarDay(day: string): Date {
  return new Date(`${day}T00:00:00`);
}

/**
 * Inclusive LocalDate bounds for a month range.
 * @param fromMonth `YYYY-MM`
 * @param toMonth `YYYY-MM`
 * @returns `fromDay` / `toDay` as `YYYY-MM-DD`
 */
export function monthToLocalDateRange(
  fromMonth: string,
  toMonth: string,
): { fromDay: string; toDay: string } {
  const [toYear, toMonthNum] = toMonth.split('-').map(Number);
  return {
    fromDay: `${fromMonth}-01`,
    toDay: formatCalendarDay(
      toYear,
      toMonthNum,
      lastDayOfMonth(toYear, toMonthNum),
    ),
  };
}

/**
 * Convert an inclusive calendar-day range to ISO datetimes.
 * Bounds use the **local** timezone (start of `fromDay`, end of `toDay`),
 * then are serialized with `.toISOString()` (UTC).
 *
 * @param fromDay `YYYY-MM-DD`
 * @param toDay `YYYY-MM-DD`
 */
export function dayToIsoRange(
  fromDay: string,
  toDay: string,
): IsoDateTimeRange {
  return {
    fromDate: parseLocalCalendarDay(fromDay).toISOString(),
    toDate: new Date(`${toDay}T23:59:59.999`).toISOString(),
  };
}

/**
 * Convert an inclusive calendar-month range to ISO datetimes.
 * @param fromMonth `YYYY-MM`
 * @param toMonth `YYYY-MM`
 */
export function monthToIsoRange(
  fromMonth: string,
  toMonth: string,
): IsoDateTimeRange {
  const { fromDay, toDay } = monthToLocalDateRange(fromMonth, toMonth);
  return dayToIsoRange(fromDay, toDay);
}

/**
 * Convert a calendar day from a date input to an ISO datetime at local start of day.
 * @param day `YYYY-MM-DD`
 */
export function calendarDayToIsoDateTimeStart(day: string): string | undefined {
  const trimmed = day.trim();
  if (trimmed === '') {
    return undefined;
  }

  if (dateInputToUnixMillis(trimmed) === undefined) {
    return undefined;
  }

  return parseLocalCalendarDay(trimmed).toISOString();
}

/**
 * Convert a calendar day from a date input to an ISO datetime at local end of day.
 * @param day `YYYY-MM-DD`
 */
export function calendarDayToIsoDateTimeEnd(day: string): string | undefined {
  const trimmed = day.trim();
  if (trimmed === '') {
    return undefined;
  }

  if (dateInputToUnixMillis(trimmed) === undefined) {
    return undefined;
  }

  return new Date(`${trimmed}T23:59:59.999`).toISOString();
}

/**
 * Convert optional calendar-day validity fields to ISO datetimes for API requests.
 * @param input `validFrom` / `validTo` as `YYYY-MM-DD` from date inputs
 */
export function calendarValidityToIsoRange(input: {
  validFrom?: string;
  validTo?: string;
}): { validFrom?: string; validTo?: string } {
  const validFrom = calendarDayToIsoDateTimeStart(input.validFrom ?? '');
  const validTo = calendarDayToIsoDateTimeEnd(input.validTo ?? '');

  return {
    ...(validFrom ? { validFrom } : {}),
    ...(validTo ? { validTo } : {}),
  };
}

/**
 * Convert a calendar date to Unix epoch milliseconds at local midnight.
 * @param value `YYYY-MM-DD`
 */
export function dateInputToUnixMillis(value: string): number | undefined {
  const trimmed = value.trim();
  if (trimmed === '') {
    return undefined;
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (!match) {
    return undefined;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return undefined;
  }

  return date.getTime();
}

/**
 * Convert Unix epoch milliseconds (or parseable date string) to a calendar date.
 * @returns `YYYY-MM-DD`, or `''` when the value cannot be converted.
 */
export function unixMillisToDateInput(
  value: number | string | null | undefined,
): string {
  if (value == null) {
    return '';
  }

  let millis: number;
  if (typeof value === 'number') {
    millis = value;
  } else {
    const trimmed = value.trim();
    if (trimmed === '') {
      return '';
    }

    const dayMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
    if (dayMatch) {
      return `${dayMatch[1]}-${dayMatch[2]}-${dayMatch[3]}`;
    }

    const asNumber = Number(trimmed);
    millis = Number.isFinite(asNumber) ? asNumber : Date.parse(trimmed);
  }

  if (!Number.isFinite(millis)) {
    return '';
  }

  const date = new Date(millis);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return toCalendarDay(date);
}
